import type Router from 'vue-router';
import lodash_debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import {
  OrganizationTypes,
  OrganizationTypesTranslate,
  UserRoles,
  MenuLinks,
} from '@/enums';
import {
  OrganizationStatuses,
  OrganizationStatusesTranslate,
} from '@/enums/statuses/organizations';
import { OptionalCol, PaginationProps, TableOptions } from '@/types';
import { KeyCloak } from '@/plugins/keycloak/types';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const debounce = lodash_debounce;

export const is_equal = isEqual;

export const clone_deep = cloneDeep;

export const translate_orgz_types = (v: OrganizationTypes) => OrganizationTypesTranslate[v];

export const translate_orgz_statuses = (v: OrganizationStatuses) =>
  OrganizationStatusesTranslate[v];

export const file_to_base64 = (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

/**
 * генерирует префикс для одиночных ячеек в вертикальной таблице
 * @param { string } col название колонки
 * @returns { string } префикс колонки
 */
export const single_slot_name = (col: string): string => `single-item-${col}`;

/**
 * склонение слова "товар"
 * @param { number | string } num число, относительно которого склоняем
 */
export const product_declension = (num: number | string) => {
  const expressions = [
    'товар', 'товара', 'товаров',
  ];
  const n = typeof num === 'string' ? Number(num) : num;

  let count = n % 100;

  if (count >= 5 && count <= 20) {
    return expressions[2];
  }
  count %= 10;

  if (count === 1) {
    return expressions[0];
  }
  if (count >= 2 && count <= 4) {
    return expressions[1];
  }
  return expressions[2];
};

/**
 * скопировать текст в буфер обмена
 * @param { string | number } text текст для копирования
 * @param { function } cb колбэк
 * @returns { Promise<void> } Promise
 */
export const copy_to_clipboard = (text: string | number, cb?: () => unknown): Promise<void> =>
  navigator.clipboard.writeText(String(text)).then(
    () => {
      if (cb) {
        cb();
      }
    },
    (err) => {
      console.error('Async: Could not copy text: ', err);
    },
  );

/**
 * удаление маски для отправки на сервер. для номера телефона остается +
 * @param { string | undefined} str - строка с маской
 * @returns { string } string | undefined
 */
export const del_mask = (str: string | undefined): string => {
  if (!str) {
    return '';
  }
  return str.replace(/[^\d+]/g, '');
};

/**
 * @returns { string[] } все внутренние роли
 */
export const all_internal_roles = Object.values(UserRoles.Internal);

/**
 * @returns { string[] } все внешние роли
 */
export const all_external_roles = Object.values(UserRoles.External);

/**
 * @returns { string[] } все пользовательские роли
 */
export const all_roles = [
  ...all_internal_roles, ...all_external_roles,
];

/**
 * Список типов организации
 * используется при выборе через radio кнопки
 */
export const orgz_radios = [
  {
    value: OrganizationTypes.ORGANIZATION,
    label: translate_orgz_types(OrganizationTypes.ORGANIZATION),
  },
  {
    value: OrganizationTypes.IND_ENTREPRENEUR,
    label: translate_orgz_types(OrganizationTypes.IND_ENTREPRENEUR),
  },
  {
    value: OrganizationTypes.INDIVIDUAL,
    label: translate_orgz_types(OrganizationTypes.INDIVIDUAL),
  },
];

/**
 * Конвертация объекта в FormData
 * @param { Record<string, any> } obj
 * @returns { FormData }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
// export const prepare_formdata = (obj: Record<string, any>): FormData => {
//   const form_data = new FormData();
//   const keys = Object.keys(obj);

//   keys.forEach((key) => {
//     if (Array.isArray(obj[key]) && obj[key].length) {
//       // eslint-disable-next-line @typescript-eslint/no-explicit-any
//       obj[key].forEach((el: Record<string, any>, idx: number) => {
//         const nested_keys = Object.keys(el);

//         nested_keys.forEach((key, i) => {
//           form_data.append(`arr[${idx}]${key}`, el[key]);
//         });
//       });
//     } else {
//       form_data.append(key, obj[key]);
//     }
//   });

//   return form_data;
// };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const prepare_formdata = (obj: Record<string, any>): FormData => {
  const form_data = new FormData();
  const keys = Object.keys(obj);

  keys.forEach((key) => {
    if (Array.isArray(obj[key]) && obj[key].length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      obj[key].forEach((el: Record<string, any> | any, idx: number) => {
        if (key === 'files') {
          form_data.append(`${key}[]`, el);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          obj[key].forEach((el: Record<string, any>, idx: number) => {
            const nested_keys = Object.keys(el);

            nested_keys.forEach((key_nest, i) => {
              form_data.append(`${key}[${idx}][${key_nest}]`, el[key_nest]);
            });
          });
        }
      });
    } else {
      form_data.append(key, obj[key]);
    }
  });

  return form_data;
};

/**
 * Получить расширение файла
 * работает быстрее, чем split('.').pop()
 * @param { string } fname
 * @returns { string } расширение файла
 */
export const get_file_extention = (fname: string): string =>
  fname.slice(((fname.lastIndexOf('.') - 1) >>> 0) + 2);

/**
 * Получить название файла из URL вместе с расширением
 * @param { string } url
 * @returns { string } расширение файла
 */
export const filename_from_url = (url: string): string => url.substring(url.lastIndexOf('/') + 1);

/**
 * Проверка значения, является ли файлом
 * @param { string | null | File | undefined } val
 * @returns { boolean }
 */
export const is_file = (val: string | null | File | undefined) =>
  !!(val instanceof File) && val !== null && val;

/**
 * Значение по умолчанию для опций таблицы
 * @returns { TableOptions }
 */
export const default_table_opts = (): TableOptions => ({
  groupBy: [],
  groupDesc: [],
  itemsPerPage: 10,
  multiSort: false,
  mustSort: false,
  page: 1,
  sortBy: [],
  sortDesc: [],
});

/**
 * Значение по умолчанию для пагинации
 * @returns { PaginationProps }
 */
export const default_pagination_props = (): PaginationProps => ({
  current_page: 1, // текущая страница
  total_pages: 1, // общее кол-во страниц
  total_items: 0, // общее кол-во элементов
  limit: 10, // кол-во на странице
});

/**
 * функция нужна для отмены сортировки в таблицах,
 * где реализована сортировка на бэке.
 *
 * @param items - элементы таблицы
 * @returns items[]
 */
export const prevent_sort = (items: unknown[]): unknown[] => items;

/**
 * @param { number } total_items - общее количество элементов
 * @param { number } page_size - количество элементов на странице
 * @returns number
 */
export const get_pages_count = (total_items: number, page_size: number): number =>
  (total_items % page_size === 0 ? total_items / page_size : ~~(total_items / page_size) + 1);

/**
 * функция для определения типа пользователя
 * @param p:string - значение параметра в url для определения характера пользователя
 * @returns boolean
 */

export const check_user_internal = (p: string): boolean => {
  const client_url = window.location.href.toString();

  return (
    client_url.includes(p)
    || localStorage.getItem(KeyCloak.TokenPrefix.USER_TYPE) === KeyCloak.UserTypes.INTERNAL
  );
};

/**
 * функция для скачивания файла
 * @param { string } path
 * @param { string } name
 */
export const download_file = (path: string, name = '') => {
  const link = document.createElement('a');

  link.href = path;
  link.setAttribute('download', `${name}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const scrollbarWidth = function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div');

  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  // outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');

  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);

  // Removing temporary elements from the DOM
  if (outer.parentNode) {
    outer.parentNode.removeChild(outer);
  }

  return scrollbarWidth;
};

dayjs.locale('ru');

export const format_date_by_genitive_case = (new_date: string) => {
  if (typeof new_date === 'string') {
    let parsed_date = dayjs(new_date, 'YYYY-MM-DD', true);

    if (!parsed_date.isValid()) {
      parsed_date = dayjs(new_date);
    }

    if (parsed_date.isValid()) {
      return parsed_date.locale('ru').format('DD MMMM YYYY').replace('г.', 'года');
    }
  }

  return '';
};

export const set_pagination_limit = (cols: OptionalCol[], value: number, storageName: string) => {
  cols.forEach((col) => {
    col.pagination_limit = value;
  });

  localStorage.setItem(storageName, JSON.stringify(cols));

  return cols;
};

export const get_pagination_limit = (storage_name: string) => {
  const stored_cols = localStorage.getItem(storage_name);
  const parsed_cols = JSON.parse(stored_cols ?? '[]');
  const first_col = parsed_cols[0];

  return first_col?.pagination_limit;
};

export const format_phone = (phone: string) => {
  const regex = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;

  if (regex.test(phone)) {
    return phone;
  }

  const cleaned = phone.replace(/\D+/g, '');
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/);

  if (match) {
    return `+7 (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`;
  }

  return phone;
};

export const current_year = () => new Date().getFullYear();

export const current_quarter = () => Math.ceil((new Date().getMonth() + 1) / 3);

export const current_date = () => dayjs().format('YYYY-MM-DD');

export const correct_date_format = (date: string) => {
  if (!date) {
    return '';
  }

  return dayjs(date).format('DD.MM.YYYY');
};

/**
 * вычисляет дату доставки (следующий день) с учетом недоступных дат
 */
export const calculate_delivery_date = (shipping_date: string, unavailable_dates: string[]): string => {
  let delivery_date = dayjs(shipping_date).add(1, 'day');

  const unavailable_set = new Set(unavailable_dates);

  while (unavailable_set.has(delivery_date.format('YYYY-MM-DD'))) {
    delivery_date = delivery_date.add(1, 'day');
  }

  return delivery_date.format('YYYY-MM-DD');
};

/**
 * Открывает детальную страницу в новой вкладке
 */
export const open_page_in_new_tab = (router: Router, route_name: string, id: string) => {
  if (id) {
    const url = router.resolve({
      name: route_name,
      params: { id },
    }).href;

    window.open(url, '_blank');
  }
};

export const normalize_datepicker_range = (dates: string[]): string[] => {
  if (dates.length !== 2) return dates;

  const [
    start, end,
  ] = dates.map((date) => new Date(date));

  return start > end ? [
    dates[1], dates[0],
  ] : dates;
};

/* eslint-disable */
/**
 * Получение слага станицы по ее роуту
*/
const route_to_slug = Object.fromEntries(
  Object.entries(MenuLinks).map(([
    key, value,
  ]) => [
      value, key,
    ]),
);

export const get_page_slug = (route_name: string) => route_to_slug[route_name];
/* eslint-enable */

export function bytesToMegabytes(bytes: number) {
  return bytes / (1024 * 1024);
}

// Возвращает сегодняшнюю дату в формате YYYY-MM-DD
export const today_date = () => dayjs().format('YYYY-MM-DD');

// Генератор тайсслотов по дате с шагом (часовой пояс мск)
export const generate_time_slots = (date: string = dayjs().tz('Europe/Moscow').format('YYYY-MM-DD'), step_minutes = 15) => {
  const nowMoscow = dayjs().tz('Europe/Moscow');
  const selected_date = dayjs.tz(date, 'YYYY-MM-DD', 'Europe/Moscow').startOf('day');
  const today = nowMoscow.clone().startOf('day');

  let start;

  if (selected_date.isAfter(today)) {
    start = selected_date.startOf('day');
  } else if (selected_date.isSame(today)) {
    start = nowMoscow.add(step_minutes - (nowMoscow.minute() % step_minutes), 'minute').startOf('minute');
  } else {
    return [];
  }

  const end = selected_date.endOf('day');
  const time_slots = [];

  while (start.isBefore(end)) {
    time_slots.push(start.format('HH:mm'));
    start = start.add(step_minutes, 'minute');
  }

  return time_slots;
};
